import {
    Badge,
    Box,
    BoxProps,
    Button,
    Icon,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    VStack,
} from "@chakra-ui/react"
import { useDownloadScreenshot } from "hook/useScreenshot"
import React, { Children, useRef } from "react"
import useDimensions from "react-cool-dimensions"
import { CgChevronDownO } from "react-icons/cg"
import { RiShareBoxLine } from "react-icons/ri"
import { MotionBox } from "./Motion"

interface MagicGroupProps extends BoxProps {
    children: React.ReactChild | React.ReactFragment | null | undefined
    hasSummary?: boolean
    isDisabled?: boolean
    isShareable?: boolean
    contentWidth?: string
}

function MagicGroup({ children, hasSummary, isDisabled, isShareable, contentWidth }: MagicGroupProps) {
    // make children array
    const arrayChildren = Children.toArray(children)

    // make children array from the second child
    const arrayChildrenFromSecond = arrayChildren.slice(1)
    // which array to render in popover
    const arrayToRender = hasSummary ? arrayChildrenFromSecond : arrayChildren

    // TODO: delete alternative below code
    // const arraySecondaryChildren = arrayChildren.filter((value, i) => i !== 0)
    // // which array to render in popover
    // const arrayToRender = hasSummary ? arraySecondaryChildren : arrayChildren

    // adjust popover position if hasSummary
    const { observe, height } = useDimensions()

    // download screenshot
    const screenshotRef = useRef<HTMLDivElement>(null)
    const { getImage, isLoading } = useDownloadScreenshot<HTMLDivElement>(screenshotRef)

    if (!isDisabled) {
        return (
            <Popover closeOnBlur={true} offset={hasSummary ? [-8, 8] : [-4, height * -1 - 16]}>
                {hasSummary && <PopoverArrow />}
                <PopoverTrigger>
                    <Box
                        cursor={"pointer"}
                        sx={{ transition: "all 0.3s" }}
                        _hover={{
                            cursor: "pointer",
                            transform: "scale(1.04)",
                        }}
                        _active={{
                            cursor: "pointer",
                            transform: "translateY(1px) scale(0.98)",
                        }}
                        ref={observe}
                        position="relative"
                        pr={4}
                    >
                        {arrayChildren[0]}
                        <Icon
                            position="absolute"
                            right={4}
                            top={"calc(50% - 9px)"}
                            boxSize="18px"
                            opacity={0.75}
                            as={CgChevronDownO}
                        />
                    </Box>
                </PopoverTrigger>
                <PopoverContent
                    width={contentWidth}
                    borderRadius="2xl"
                    borderColor="whiteAlpha.600"
                    boxShadow="xl"
                    p={2}
                    bg="#181A1B"
                >
                    <PopoverBody ref={screenshotRef}>
                        <VStack align="stretch" spacing={3}>
                            {arrayToRender.map((item, index) => (
                                <MotionBox key={"child" + index} layoutId={"child" + index}>
                                    {item}
                                </MotionBox>
                            ))}
                            {isShareable && (
                                <Button
                                    data-html2canvas-ignore
                                    isLoading={isLoading}
                                    onClick={getImage}
                                    size="xs"
                                    alignSelf={"flex-end"}
                                    variant={"ghost"}
                                    rightIcon={<Icon as={RiShareBoxLine} />}
                                >
                                    Share <Badge ml={1}>WIP</Badge>
                                </Button>
                            )}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        )
    } else {
        return <>{children}</>
    }
}

export { MagicGroup }
